import { defineStore } from 'pinia'
import { markRaw, ref, type Component } from 'vue'

/**
 * Modal store for managing programmatic modals with promise-based results.
 *
 * This store is used by layouts to render modals that are opened via `modalStore.openModal()`.
 * It provides a promise-based API for modal results and supports modal stacking.
 *
 * @example
 * // Open a modal and wait for result
 * const result = await modalStore.openModal(SomeModal, { userId: 123 })
 *
 * @example
 * // Close modal with data
 * modalStore.closeModal({ confirmed: true })
 */

interface ModalItem<T = any> {
    id: number
    component: Component
    props: Record<string, unknown>
    resolve?: (value?: T) => void
    reject?: (reason?: any) => void
}

export const useModalStore = defineStore('modal', () => {
    const modalStack = ref<ModalItem[]>([])
    let modalIdCounter = 0

    /**
     * Opens a modal and returns a promise that resolves when the modal is closed.
     * @param component - Vue component to render as modal
     * @param props - Props to pass to the modal component
     * @returns Promise that resolves with the data passed to closeModal()
     */
    function openModal(component: Component, props = {}): Promise<any> {
        return new Promise((resolve, reject) => {
            const modalId = modalIdCounter++

            modalStack.value.push({
                id: modalId,
                component: markRaw(component),
                props,
                resolve,
                reject
            })
        })
    }

    /**
     * Closes a modal by ID or the top modal in the stack.
     * @param idOrData - Either modal ID (number) to close specific modal, or data to pass to top modal
     */
    function closeModal(idOrData?: number | any) {
        if (modalStack.value.length === 0) return

        // If a number is passed, treat it as modal ID
        if (typeof idOrData === 'number') {
            const modalIndex = modalStack.value.findIndex((m) => m.id === idOrData)
            if (modalIndex !== -1) {
                const modal = modalStack.value.splice(modalIndex, 1)[0]
                modal?.resolve?.(undefined)
            }
        } else {
            // Otherwise, close top modal with data
            const topModal = modalStack.value.pop()!
            if (topModal.resolve) topModal.resolve(idOrData)
        }
    }

    /**
     * Cancels a specific modal by ID, rejecting its promise.
     * @param modalId - ID of the modal to cancel
     * @param error - Error to reject the promise with
     */
    function cancelModal(modalId: number, error?: any) {
        const modalIndex = modalStack.value.findIndex((m) => m.id === modalId)
        if (modalIndex !== -1) {
            const modal = modalStack.value.splice(modalIndex, 1)[0]
            modal?.reject?.(error)
        }
    }

    /**
     * Closes the topmost modal (alias for closeModal).
     * @param data - Data to resolve the modal promise with
     */
    function closeTopModal(data?: any) {
        closeModal(data)
    }

    return { modalStack, openModal, closeModal, cancelModal, closeTopModal }
})
